/**
 * Public Hotel Verification Trust Card
 *
 * Route: /verify/hotel/:hotelId   (zero auth, zero PII, publicly cacheable)
 *
 * Designed for sales decks + venue / partner due-diligence. A third party can
 * hit this URL and confirm the hotel is:
 *   - Verified in our system (off-chain trust score)
 *   - Onboarded on-chain (signer registered on ArgusAttestationRegistry)
 *   - Actively producing signed attestations (30-day rolling counts)
 *   - Holds a HOTEL Reputation SBT (if minted)
 *
 * Backend: GET /api/public/verify/hotel/{hotel_id}
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ShieldCheck, ShieldAlert, ShieldX, ExternalLink, Hash, Building2,
  Activity, Sparkles, Globe, Copy, Check, Calendar,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { API } from '@/lib/backendUrl';

const VERDICT_META = {
  active_attested: {
    label: 'Active & on-chain attested',
    color: 'emerald',
    Icon: ShieldCheck,
    blurb: 'This hotel is fully verified, registered on-chain, and is actively producing cryptographically signed operational attestations.',
  },
  onboarded_quiet: {
    label: 'On-chain · awaiting attestations',
    color: 'amber',
    Icon: ShieldCheck,
    blurb: 'This hotel is verified and onboarded on-chain. No attestations have been recorded in the last 30 days yet.',
  },
  verified_offchain_only: {
    label: 'Verified · off-chain only',
    color: 'cyan',
    Icon: ShieldCheck,
    blurb: 'This hotel passed our verification pipeline but has not yet been onboarded to the on-chain attestation registry.',
  },
  pending: {
    label: 'Verification pending',
    color: 'zinc',
    Icon: ShieldAlert,
    blurb: 'This hotel has been registered but has not yet completed verification.',
  },
  unverified: {
    label: 'Not verified',
    color: 'rose',
    Icon: ShieldX,
    blurb: 'This hotel did not pass verification. Treat any claims with caution.',
  },
};

const COLOR_CLASSES = {
  emerald: { border: 'border-emerald-500/40', bg: 'bg-emerald-500/10', text: 'text-emerald-300', accent: 'text-emerald-400' },
  amber:   { border: 'border-amber-500/40',   bg: 'bg-amber-500/10',   text: 'text-amber-300',   accent: 'text-amber-400' },
  cyan:    { border: 'border-cyan-500/40',    bg: 'bg-cyan-500/10',    text: 'text-cyan-300',    accent: 'text-cyan-400' },
  zinc:    { border: 'border-zinc-500/40',    bg: 'bg-zinc-700/30',    text: 'text-zinc-300',    accent: 'text-zinc-400' },
  rose:    { border: 'border-rose-500/40',    bg: 'bg-rose-500/10',    text: 'text-rose-300',    accent: 'text-rose-400' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

function shortAddr(addr) {
  if (!addr) return '—';
  if (addr.length < 12) return addr;
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

export default function VerifyHotelPage() {
  const { hotelId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/public/verify/hotel/${hotelId}`);
      if (r.status === 404) {
        setError('Hotel not found in the BHERMES registry.');
        return;
      }
      if (!r.ok) {
        throw new Error(`HTTP ${r.status}`);
      }
      const d = await r.json();
      setData(d);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [hotelId]);

  useEffect(() => { load(); }, [load]);

  const verdictMeta = useMemo(() => {
    if (!data) return null;
    return VERDICT_META[data.verdict] || VERDICT_META.pending;
  }, [data]);

  const copyShareLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {/* ignore */}
  }, []);

  // ─── Render ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] text-zinc-300 flex items-center justify-center" data-testid="verify-hotel-loading">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <div className="text-sm font-mono text-zinc-500">Loading trust card…</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] text-zinc-300 flex items-center justify-center px-4" data-testid="verify-hotel-error">
        <Card className="bg-zinc-900/50 border-rose-500/40 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <ShieldX className="w-12 h-12 text-rose-500 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-zinc-100 mb-2">Verification unavailable</h1>
            <p className="text-sm text-zinc-400 mb-4">{error}</p>
            <Button onClick={() => nav('/')} className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold" data-testid="verify-hotel-home-btn">
              Back to getbitsy.ai
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { hotel, signer, registry, attestations_30d, sbt, is_onboarded_onchain, trust_score } = data;
  const c = COLOR_CLASSES[verdictMeta.color];
  const VerdictIcon = verdictMeta.Icon;

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-zinc-200" data-testid="verify-hotel-page">
      {/* Header bar */}
      <div className="border-b border-zinc-800 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-amber-400" />
          <div>
            <div className="font-bold text-zinc-100 text-sm tracking-wider">BHERMES PROTOCOL</div>
            <div className="text-[10px] font-mono text-zinc-500 -mt-0.5">Public hotel verification</div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={copyShareLink}
              className="text-zinc-400 hover:text-zinc-100"
              data-testid="verify-hotel-copy-btn"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              <span className="ml-1.5 text-xs">{copied ? 'Copied' : 'Copy link'}</span>
            </Button>
            <a href="https://getbitsy.ai" target="_blank" rel="noopener noreferrer" className="text-xs text-zinc-400 hover:text-amber-400 flex items-center gap-1" data-testid="verify-hotel-home-link">
              getbitsy.ai <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Hero verdict card */}
        <Card className={`${c.bg} ${c.border} border-2`} data-testid="verify-hotel-verdict-card">
          <CardContent className="p-6">
            <div className="flex items-start gap-5">
              <div className={`p-3 rounded-xl ${c.bg} ${c.border} border`}>
                <VerdictIcon className={`w-10 h-10 ${c.accent}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Building2 className="w-4 h-4 text-zinc-400" />
                  <h1 className="text-2xl font-bold text-zinc-100 truncate" data-testid="verify-hotel-name">
                    {hotel.name}
                  </h1>
                </div>
                <div className="text-sm text-zinc-400 mb-3">
                  {hotel.city}{hotel.country ? ` · ${hotel.country}` : ''}
                </div>
                <Badge className={`${c.bg} ${c.text} ${c.border} font-mono text-xs`} data-testid="verify-hotel-verdict-badge">
                  {verdictMeta.label}
                </Badge>
                <p className={`mt-3 text-sm ${c.text} leading-relaxed max-w-2xl`}>
                  {verdictMeta.blurb}
                </p>
              </div>
              {hotel.hero_photo && (
                <img
                  src={hotel.hero_photo.startsWith('http') ? hotel.hero_photo : `${API}/api/photos/serve/${hotel.hero_photo}`}
                  alt={hotel.name}
                  className="w-32 h-32 rounded-lg object-cover border border-zinc-700 hidden md:block"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Stat row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6" data-testid="verify-hotel-stats">
          <StatCell
            label="Trust score"
            value={`${trust_score}/100`}
            color={trust_score >= 80 ? 'emerald' : trust_score >= 50 ? 'amber' : 'rose'}
            Icon={ShieldCheck}
          />
          <StatCell
            label="On-chain"
            value={is_onboarded_onchain ? 'Registered' : 'Not yet'}
            color={is_onboarded_onchain ? 'emerald' : 'zinc'}
            Icon={Hash}
          />
          <StatCell
            label="Attestations 30d"
            value={attestations_30d.total}
            color={attestations_30d.total > 0 ? 'cyan' : 'zinc'}
            Icon={Activity}
          />
          <StatCell
            label="Reputation SBT"
            value={sbt ? (sbt.tier || 'Minted') : 'Not minted'}
            color={sbt ? 'violet' : 'zinc'}
            Icon={Sparkles}
          />
        </div>

        {/* Detail row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          {/* Signer card */}
          <Card className="bg-zinc-900/50 border-zinc-800" data-testid="verify-hotel-signer-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Hash className="w-4 h-4 text-zinc-400" />
                <h2 className="font-mono text-xs uppercase tracking-wider text-zinc-300">On-chain signer</h2>
              </div>
              {signer && signer.address ? (
                <>
                  <div className="text-[10px] font-mono text-zinc-500">Signer address</div>
                  <div className="font-mono text-sm text-amber-200 break-all mb-3" data-testid="verify-hotel-signer-addr">
                    {shortAddr(signer.address)}
                  </div>
                  {signer.registration_tx && (
                    <a
                      href={`https://sepolia.basescan.org/tx/${signer.registration_tx}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1 mb-2"
                      data-testid="verify-hotel-signer-tx-link"
                    >
                      Registration tx <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  {signer.registered_at && (
                    <div className="text-[10px] font-mono text-zinc-500">
                      <Calendar className="inline w-3 h-3 mr-1" />
                      Registered {fmtDate(signer.registered_at)}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-sm text-zinc-500 italic">
                  Not yet onboarded to the on-chain attestation registry.
                </div>
              )}
              <div className="mt-4 pt-3 border-t border-zinc-800 text-[10px] font-mono text-zinc-500">
                <div>Registry · <span className="text-zinc-400">{registry.chain}</span></div>
                <a
                  href={registry.explorer}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyan-400 hover:text-cyan-300 flex items-center gap-1 mt-0.5"
                  data-testid="verify-hotel-registry-link"
                >
                  {shortAddr(registry.address)} <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </CardContent>
          </Card>

          {/* Attestation breakdown */}
          <Card className="bg-zinc-900/50 border-zinc-800" data-testid="verify-hotel-attest-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Activity className="w-4 h-4 text-zinc-400" />
                <h2 className="font-mono text-xs uppercase tracking-wider text-zinc-300">Attestation activity (30d)</h2>
              </div>
              {attestations_30d.total === 0 ? (
                <div className="text-sm text-zinc-500 italic">
                  No attestations recorded in the last 30 days.
                </div>
              ) : (
                <>
                  <div className="space-y-1.5 mb-3" data-testid="verify-hotel-attest-breakdown">
                    {Object.entries(attestations_30d.by_type).map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between text-xs">
                        <span className="font-mono text-zinc-400">{type}</span>
                        <Badge variant="outline" className="border-zinc-700 text-zinc-300 font-mono">
                          {count}
                        </Badge>
                      </div>
                    ))}
                  </div>
                  {attestations_30d.last_attestation && (
                    <div className="text-[10px] font-mono text-zinc-500 pt-3 border-t border-zinc-800">
                      <Calendar className="inline w-3 h-3 mr-1" />
                      Last: {attestations_30d.last_attestation.event_type} · {fmtDate(attestations_30d.last_attestation.created_at)}
                      {attestations_30d.last_attestation.signed && (
                        <Badge className="ml-2 bg-emerald-500/10 text-emerald-300 border-emerald-500/30 text-[9px]">
                          EIP-712 signed
                        </Badge>
                      )}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* SBT card if present */}
        {sbt && (
          <Card className="bg-zinc-900/50 border-violet-500/30 mt-4" data-testid="verify-hotel-sbt-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-violet-400" />
                <h2 className="font-mono text-xs uppercase tracking-wider text-violet-200">HOTEL Reputation SBT</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <Kv label="SBT id" value={shortAddr(sbt.sbt_id || '—')} />
                <Kv label="Tier" value={sbt.tier || '—'} />
                <Kv label="Minted" value={fmtDate(sbt.minted_at)} />
                <Kv label="Clean streak" value={String(sbt.current_clean_streak ?? '—')} />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer / how this works */}
        <Card className="bg-zinc-900/30 border-zinc-800 mt-6">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="w-4 h-4 text-amber-400" />
              <h2 className="font-mono text-xs uppercase tracking-wider text-amber-200">How verification works</h2>
            </div>
            <ol className="text-xs text-zinc-400 leading-relaxed space-y-1 pl-4 list-decimal">
              <li>Hotel registers on <a href="https://getbitsy.ai" className="text-cyan-400 hover:underline" target="_blank" rel="noopener noreferrer">getbitsy.ai</a> — Hermes Agent scores 4 dimensions (legitimacy, location, online presence, photos).</li>
              <li>Score ≥85 → auto-approval, HOTEL SBT minted, tokenomics triggered (Era 1: 100K HERMES, 3-bucket split).</li>
              <li>Pilot onboarding generates a per-hotel signer key, registers it on the <code className="bg-zinc-800 px-1 rounded text-amber-300">ArgusAttestationRegistry</code> (Base Sepolia).</li>
              <li>Every operational event (cleaning, restock, ID-verify, IoT shrinkage) is EIP-712 signed and counted here.</li>
              <li>This page is public, zero-PII, and refreshes on demand — share it with venues, partners, or anyone doing due diligence.</li>
            </ol>
            <div className="text-[10px] font-mono text-zinc-600 mt-4 flex flex-wrap gap-x-4 gap-y-1">
              <span>Generated {data.generated_at}</span>
              <span>·</span>
              <a href={registry.explorer} target="_blank" rel="noopener noreferrer" className="text-cyan-500 hover:text-cyan-300">
                Verify the registry contract on BaseScan ↗
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function StatCell({ label, value, color, Icon }) {
  const c = COLOR_CLASSES[color] || COLOR_CLASSES.zinc;
  return (
    <div className={`${c.bg} ${c.border} border rounded-lg p-3`} data-testid={`verify-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-1">
        <Icon className={`w-3 h-3 ${c.accent}`} />
        {label}
      </div>
      <div className={`text-lg font-bold font-mono ${c.text}`}>{value}</div>
    </div>
  );
}

function Kv({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">{label}</div>
      <div className="text-sm font-mono text-zinc-200 truncate">{value}</div>
    </div>
  );
}
